/**
 * The one `fetch` wrapper for `/api`, shared by platform routes and games' content routes
 * (exported through `@gsf/kit`). The browser sends the `HttpOnly` login cookie itself.
 */

/** Carries the status so callers can tell "no such session" from "no network". */
export class ApiError extends Error {
  readonly status: number;
  /** The server's machine-readable reason, e.g. `'signin'`, which wants a form, not a toast. */
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** True when the only thing wrong with this request is that nobody is signed in. */
export function needsSignIn(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    // The default, stated: a future absolute URL would silently drop the login cookie.
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: string; code?: string }
      | null;
    throw new ApiError(
      body?.error ?? `Request failed (${res.status})`,
      res.status,
      body?.code ?? null,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
